import { z } from "zod"

import type {
  ChartAxisKind,
  ChartSeriesColor,
  ChartType,
  ChartValueFormat,
  CovalChartSpecV1,
} from "../model/chart/types"

/**
 * CovalChartSpec v1 validation + closed resolution maps. The wire
 * TYPES live in `model/chart/types.ts`; this file owns the fail-closed zod schema
 * (its output is compile-checked against the wire type by parseChartSpec's
 * return annotation), the caps, and the closed color/format maps. The FE is
 * the ONLY interpreter — the spec is data, never code. Every object level is
 * `.strict()` (unknown keys fail), every string is length-capped, colors and
 * value formats are CLOSED ENUMS resolved to FE-owned tokens/formatters
 * below, and the whole spec is size-capped. `parseChartSpec` is fail-closed:
 * anything invalid returns null and the caller (utils/actions.ts) DROPS the
 * proposal — identical semantics to an unknown action kind, so a broken spec
 * never reaches the renderer.
 *
 * Kept in lockstep with sofia-agent's `render_visualization` action spec
 * (product/actions.py): same kind string, args `{title, spec}`, and the
 * server stamps `requires_confirmation: false` (auto-execute gate).
 */

export type { ChartValueFormat, CovalChartSpecV1 } from "../model/chart/types"

/** Hard cap on the serialized spec (JSON.stringify length, ~16KB). */
export const MAX_CHART_SPEC_SERIALIZED_LENGTH = 16_384
const MAX_DATA_ROWS = 100
const MAX_ROW_KEYS = 12
const MAX_SERIES = 8
/** Pie charts render at most this many slices; the tail collapses to "Other". */
export const MAX_PIE_SLICES = 8
/**
 * Default inline chart height (px). Lives here — not in CoviVizChartBody — so the
 * card's lazy-loading skeleton can size itself without statically importing the
 * recharts bundle.
 */
export const DEFAULT_CHART_HEIGHT = 280

// Enum member lists for the zod schema — `satisfies` pins each to the wire
// unions in model/chart/types.ts so the schema can't drift from the type.
const CHART_TYPES = [
  "bar",
  "line",
  "area",
  "pie",
  "scatter",
  "stat",
] as const satisfies readonly ChartType[]
const AXIS_KINDS = [
  "category",
  "time",
  "number",
] as const satisfies readonly ChartAxisKind[]
const VALUE_FORMATS = [
  "number",
  "integer",
  "percent",
  "seconds",
  "ms",
  "usd",
  "date",
  "datetime",
] as const satisfies readonly ChartValueFormat[]
const SERIES_COLORS = [
  "primary",
  "blue",
  "green",
  "red",
  "pink",
  "amber",
  "muted",
] as const satisfies readonly ChartSeriesColor[]

// Keys that must never enter a data row: JSON.parse creates them as OWN
// properties, and letting them through invites prototype-pollution the moment
// anyone spreads a row onto defaults.
const UNSAFE_ROW_KEYS = new Set(["__proto__", "constructor", "prototype"])

const rowKeySchema = z
  .string()
  .min(1)
  .max(40)
  .refine((key) => !UNSAFE_ROW_KEYS.has(key), {
    message: "unsafe data row key",
  })

/** Cells are primitives only — no nested objects/arrays ever reach the renderer. */
const cellSchema = z.union([
  z.string().max(200),
  z.number().finite(),
  z.boolean(),
  z.null(),
])

const rowSchema = z.record(rowKeySchema, cellSchema).superRefine((row, ctx) => {
  if (Object.keys(row).length > MAX_ROW_KEYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `data rows are capped at ${MAX_ROW_KEYS} keys`,
    })
  }
})

const xAxisSchema = z
  .object({
    field: z.string().min(1).max(40),
    label: z.string().max(80).optional(),
    kind: z.enum(AXIS_KINDS).default("category"),
  })
  .strict()

const yAxisSchema = z
  .object({
    label: z.string().max(80).optional(),
    format: z.enum(VALUE_FORMATS).optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
  })
  .strict()

const seriesSchema = z
  .object({
    field: z.string().min(1).max(40),
    name: z.string().max(80).optional(),
    color: z.enum(SERIES_COLORS).optional(),
  })
  .strict()

const optsSchema = z
  .object({
    stacked: z.boolean().optional(),
    horizontal: z.boolean().optional(),
    /** Defaults to true in the renderer when absent. */
    legend: z.boolean().optional(),
    value_labels: z.boolean().optional(),
    percent_stack: z.boolean().optional(),
  })
  .strict()

const covalChartSpecSchema = z
  .object({
    v: z.literal(1),
    chart: z.enum(CHART_TYPES),
    title: z.string().min(1).max(120),
    subtitle: z.string().max(200).optional(),
    x: xAxisSchema.optional(),
    y: yAxisSchema.optional(),
    series: z.array(seriesSchema).min(1).max(MAX_SERIES),
    data: z.array(rowSchema).min(1).max(MAX_DATA_ROWS),
    opts: optsSchema.optional(),
  })
  .strict()
  .superRefine((spec, ctx) => {
    // x is the category/time axis — required for everything but the
    // big-number stat tile.
    if (spec.chart !== "stat" && !spec.x) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `x is required for chart "${spec.chart}"`,
      })
    }
    // Pie: x.field labels the slices, exactly one series carries the value.
    if (spec.chart === "pie" && spec.series.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "pie charts take exactly 1 series",
      })
    }
    // Stat: one row, one series — a single number.
    if (spec.chart === "stat") {
      if (spec.series.length !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "stat charts take exactly 1 series",
        })
      }
      if (spec.data.length !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "stat charts take exactly 1 data row",
        })
      }
    }
    // Every referenced field must exist in every data row (null cells encode
    // missing values) — a spec pointing at fields the data doesn't carry is a
    // model mistake, fail closed.
    const referencedFields = [
      ...(spec.x ? [spec.x.field] : []),
      ...spec.series.map((series) => series.field),
    ]
    for (const field of referencedFields) {
      if (!spec.data.every((row) => Object.hasOwn(row, field))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `field "${field}" is missing from the data rows`,
        })
      }
    }
  })

/**
 * Fail-closed spec parse: null means DROP (never render). Checks the size cap
 * on the raw payload first so an oversized spec never reaches zod. The return
 * annotation is the compile-time proof that the schema's inferred output
 * matches the hand-written wire type in model/chart/types.ts.
 */
export function parseChartSpec(
  value: unknown,
  options: { allowReportColors?: boolean } = {},
): CovalChartSpecV1 | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null
  }
  let serialized: string
  try {
    serialized = JSON.stringify(value) ?? ""
  } catch {
    // Circular / unserializable payloads can't have come off the wire — drop.
    return null
  }
  if (serialized.length > MAX_CHART_SPEC_SERIALIZED_LENGTH) {
    return null
  }
  const result = covalChartSpecSchema.safeParse(value)
  if (!result.success) {
    return null
  }
  if (
    !options.allowReportColors &&
    result.data.series.some((series) => series.color === "pink")
  ) {
    return null
  }
  return result.data
}

/* ── Closed color map ─────────────────────────────────────────────────────
 * The spec's color enum resolves HERE, never in CSS authored by the model.
 *
 * Each name resolves to a slot in the data-viz palette
 * (`--coval-chart-series-N` from @coval/ui/chart-tokens.css), which is spaced in
 * lightness *and* chroma so the slots stay separable under protanopia,
 * deuteranopia, and tritanopia. Chart color is an accessibility decision, so
 * these do not come from the brand accent ramps.
 *
 * Names are a wire contract — Sofia emits them — so they stay fixed even though
 * the hue behind a name may move. `primary` has no color meaning here (it
 * predates the palette), so it takes sky, the slot that leaves the widest
 * worst-case separation across the six. `muted` takes the ink ramp's neutral. */

const SERIES_SLOT: Record<ChartSeriesColor, number> = {
  amber: 1,
  blue: 2,
  primary: 3,
  red: 4,
  pink: 5,
  green: 7,
  muted: 8,
}

/**
 * Large-area fills — bars, area bodies, pie slices. The theme-aware var softens
 * to each hue's `mid` tone in light mode, which is legal for a filled area
 * because its size (plus the legend) carries the read.
 */
export const CHART_FILL_TOKENS: Record<ChartSeriesColor, string> =
  Object.fromEntries(
    Object.entries(SERIES_SLOT).map(([name, slot]) => [
      name,
      `var(--coval-chart-series-${slot})`,
    ]),
  ) as Record<ChartSeriesColor, string>

/**
 * Thin marks — line strokes, area outlines, scatter points. A 2px stroke has no
 * area to compensate with, so it owes WCAG 1.4.11's 3:1 against the canvas, and
 * no single fixed tone clears that on both canvases: the fill tone fails on
 * light, and the `strong` identity tone fails on dark for blue and — because
 * ink's `strong` *is* the dark canvas color — renders `muted` invisible. So marks
 * get their own theme-aware token, each hue's `dark` tone in light and `mid` in
 * dark.
 */
export const CHART_MARK_COLORS: Record<ChartSeriesColor, string> =
  Object.fromEntries(
    Object.entries(SERIES_SLOT).map(([name, slot]) => [
      name,
      `var(--coval-chart-mark-${slot})`,
    ]),
  ) as Record<ChartSeriesColor, string>

/** Default per-series color rotation when the spec doesn't pick one. */
export const CHART_SERIES_PALETTE: readonly ChartSeriesColor[] = [
  "blue",
  "green",
  "amber",
  "red",
  "primary",
  "muted",
]

function seriesColorName(
  color: ChartSeriesColor | undefined,
  index: number,
): ChartSeriesColor {
  return color ?? CHART_SERIES_PALETTE[index % CHART_SERIES_PALETTE.length]
}

/** Fill for a filled form — bar, area body, pie slice. */
export function resolveSeriesFill(
  color: ChartSeriesColor | undefined,
  index: number,
): string {
  return CHART_FILL_TOKENS[seriesColorName(color, index)]
}

/** Stroke or point color for a thin mark — line, area outline, scatter. */
export function resolveSeriesMark(
  color: ChartSeriesColor | undefined,
  index: number,
): string {
  return CHART_MARK_COLORS[seriesColorName(color, index)]
}

/* ── Closed formatter map ─────────────────────────────────────────────────
 * The spec's format enum resolves to these FE-owned functions — the spec can
 * never carry a formatter (recharts formatters take functions; a string that
 * became code would be an eval channel). Dates render in PST. */

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const PST_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "short",
  day: "numeric",
  year: "numeric",
})

const PST_DATETIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
})

function humanizeSeconds(totalSeconds: number): string {
  const abs = Math.abs(totalSeconds)
  if (abs < 60) {
    const rounded = Math.round(totalSeconds * 10) / 10
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}s`
  }
  if (abs < 3600) {
    const minutes = Math.trunc(totalSeconds / 60)
    // abs() the sub-unit so negative durations keep it (-70s → "-1m 10s", not "-1m").
    const seconds = Math.abs(Math.round(totalSeconds - minutes * 60))
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
  }
  const hours = Math.trunc(totalSeconds / 3600)
  const minutes = Math.abs(Math.round((totalSeconds - hours * 3600) / 60))
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
}

function toDate(value: string | number): Date | null {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Inert string form of a cell. The schema guarantees cells are primitives, so
 * anything else (defensive) renders as empty rather than "[object Object]".
 */
function stringifyCell(value: unknown): string {
  if (typeof value === "string") {
    return value
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value)
  }
  return ""
}

/**
 * Format one cell value with the spec's (validated) format enum. Non-numeric
 * values fall back to their string form so a stray string cell on a numeric
 * axis renders inert text instead of throwing.
 */
export function formatChartValue(
  value: unknown,
  format?: ChartValueFormat,
): string {
  if (value === null || value === undefined) {
    return ""
  }
  if (format === "date" || format === "datetime") {
    if (typeof value === "string" || typeof value === "number") {
      const date = toDate(value)
      if (date) {
        return format === "date"
          ? PST_DATE_FORMATTER.format(date)
          : PST_DATETIME_FORMATTER.format(date)
      }
    }
    return stringifyCell(value)
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return stringifyCell(value)
  }
  switch (format) {
    case "percent":
      return `${(value * 100).toFixed(1)}%`
    case "usd":
      return USD_FORMATTER.format(value)
    case "seconds":
      return humanizeSeconds(value)
    case "ms":
      return Math.abs(value) < 1000
        ? `${Math.round(value)}ms`
        : humanizeSeconds(value / 1000)
    case "integer":
      return Math.round(value).toLocaleString("en-US")
    case "number":
    case undefined:
      return value.toLocaleString("en-US", { maximumFractionDigits: 2 })
  }
}

/* ── CSV export ─────────────────────────────────────────────────────────── */

function escapeCsvCell(value: unknown): string {
  const text = stringifyCell(value)
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/**
 * Flatten the spec's data table to CSV for the card's Copy CSV button —
 * columns are the x field plus each series field, headed by their labels.
 */
export function toCsv(spec: CovalChartSpecV1): string {
  const columns = [
    ...(spec.x
      ? [{ field: spec.x.field, header: spec.x.label ?? spec.x.field }]
      : []),
    ...spec.series.map((series) => ({
      field: series.field,
      header: series.name ?? series.field,
    })),
  ]
  const lines = [
    columns.map((column) => escapeCsvCell(column.header)).join(","),
  ]
  for (const row of spec.data) {
    lines.push(
      columns.map((column) => escapeCsvCell(row[column.field])).join(","),
    )
  }
  return lines.join("\n")
}
